import { getSupabase } from '@/systems/SupabaseClient';

/**
 * StudentService — CRUD de alunos do responsável logado.
 *
 * RLS no banco já restringe automaticamente as queries ao parent_id =
 * auth.uid(), então não precisamos passar o user id manualmente. Mesmo
 * que esquecêssemos um filtro, o RLS bloqueia.
 *
 * Trigger SQL `handle_new_student` cria a row em student_progress
 * automaticamente — depois do insert, o aluno já está pronto pra jogar.
 */

export interface Student {
  id: string;
  displayName: string;
  createdAt: string;
}

export interface StudentWithProgress extends Student {
  /** Total de XP do aluno (0 quando ainda não jogou). */
  totalXP: number;
}

export type StudentResult<T> = { ok: true; value: T } | { ok: false; error: string };

const TABLE = 'students';

export async function listStudents(): Promise<StudentResult<Student[]>> {
  const { data, error } = await getSupabase()
    .from(TABLE)
    .select('id, display_name, created_at')
    .order('created_at', { ascending: true });
  if (error) return { ok: false, error: error.message };
  return { ok: true, value: (data ?? []).map(rowToStudent) };
}

/**
 * Lista alunos enriquecidos com XP via join 1:1 em student_progress.
 * RLS aplica scoping pelo parent_id automaticamente. Trigger SQL
 * garante que toda row em students tem sua row de progress, mas
 * mantemos default 0 caso a invariante quebre por algum motivo.
 */
export async function listStudentsWithProgress(): Promise<StudentResult<StudentWithProgress[]>> {
  const { data, error } = await getSupabase()
    .from(TABLE)
    .select('id, display_name, created_at, student_progress(total_xp)')
    .order('created_at', { ascending: true });
  if (error) return { ok: false, error: error.message };
  return { ok: true, value: (data ?? []).map(rowToStudentWithProgress) };
}

export async function createStudent(displayName: string): Promise<StudentResult<Student>> {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return { ok: false, error: 'Informe um nome ou apelido.' };
  if (trimmed.length > 40) return { ok: false, error: 'Nome muito longo (máx. 40 caracteres).' };

  const sb = getSupabase();
  const { data: userData } = await sb.auth.getUser();
  if (!userData.user) return { ok: false, error: 'Sessão expirou — entre de novo.' };

  const { data, error } = await sb
    .from(TABLE)
    .insert({ parent_id: userData.user.id, display_name: trimmed })
    .select('id, display_name, created_at')
    .single();
  if (error) return { ok: false, error: error.message };
  return { ok: true, value: rowToStudent(data) };
}

export async function deleteStudent(id: string): Promise<StudentResult<true>> {
  const { error } = await getSupabase().from(TABLE).delete().eq('id', id);
  if (error) return { ok: false, error: error.message };
  return { ok: true, value: true };
}

function rowToStudent(row: { id: string; display_name: string; created_at: string }): Student {
  return { id: row.id, displayName: row.display_name, createdAt: row.created_at };
}

interface ProgressJoinRow {
  id: string;
  display_name: string;
  created_at: string;
  // PostgREST devolve a relação como objeto (1:1 via PK FK) OU array,
  // dependendo do hint. Aceitamos os dois formatos defensivamente.
  student_progress: { total_xp: number } | { total_xp: number }[] | null;
}

function rowToStudentWithProgress(row: ProgressJoinRow): StudentWithProgress {
  const sp = row.student_progress;
  const xp = Array.isArray(sp) ? sp[0]?.total_xp ?? 0 : sp?.total_xp ?? 0;
  return { id: row.id, displayName: row.display_name, createdAt: row.created_at, totalXP: xp };
}
